import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { first } from 'lodash';
import { useHistory } from 'react-router';
import { useMutation, useQueryClient } from 'react-query';
import createPersistedState from '~/react-hooks/use-persisted-state';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';

import Context from '../../Context';
import { alertAsync } from '~/App/Components/Modals/Alert';
import assignGreetsAction from '~/actions/greets/assignGreetsAction';

import WorkspaceDropdown from '~/App/Modules/Workspaces/Components/WorkspaceSelectDropdown';
import assignWorkspaceGreetsAction from '~/actions/greets/assignWorkspaceGreetsAction';
import { Greet, GreetSendType, UserSimple } from '~/types/models';
import Workspace from '~/App/Modules/Workspaces/Types/Workspace';
import { isAxiosError } from '~/utils/Axios';
import { useDeleteGreets } from '~/react-hooks/use-delete-greets';
import { TeamMemberSelector } from '~/App/Components/Filters/TeamMemberSelector';
import { queryKeys, useDisableGreetFallback } from '~/App/Api/Greets';
import DeleteConfirmationModal from '~/App/Components/Modals/DeleteConfirmationModal';
import analytics from '~/utils/Analytics';
import {
  GREET_DISABLE_FALLBACK,
  GREET_DISABLE_FALLBACK_CONFIRM,
} from '~/generated/AnalyticsTypes';
import { useFeatures } from '~/react-hooks/use-features';
import { Tooltip } from '@vimily/bonjoro-ui-components-v2/Tooltip';
import RollUpButtonWithTooltip from '../../Routes/BulkSelectActions';

const workspaceSelection = createPersistedState('bonjoro-workspace-selection');

export interface HeaderActionsProps {
  className?: string;
  greet: Greet | Greet[];
  showAssign?: boolean;
  showWorkspace?: boolean;
  showRollUp?: boolean;
}

export default function HeaderActions({
  className = '',
  greet,
  showAssign,
  showWorkspace,
  showRollUp,
}: HeaderActionsProps) {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [greets, setGreets] = useState<Greet[]>([]);
  const [, setWorkspace] = workspaceSelection<Workspace | null>(null);
  const history = useHistory();
  const { canAccessFallbackVideos } = useFeatures();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { bulkSelect } = useContext(Context);
  const queryClient = useQueryClient();
  const { handleDeleteClick } = useDeleteGreets(greets);
  const [selectedAssignee, setSelectedAssignee] = useState<UserSimple>();
  const isFallbackVideosEnabled = canAccessFallbackVideos;

  const greetIds = useMemo(() => {
    return greets.map((greet) => greet.id);
  }, [greets]);

  const hasFallback = useMemo(() => {
    if (greets.length === 0) return false;

    return (
      typeof greets.find(
        (greet) => greet.send_type === GreetSendType.Fallback
      ) !== 'undefined'
    );
  }, [greets]);

  const selectedWorkspace = useMemo(() => {
    if (greets.length === 1) {
      return greets[0].campaign;
    }

    return undefined;
  }, [greets]);

  useEffect(() => {
    setGreets(greet instanceof Array ? greet : [greet]);
  }, [greet]);

  useEffect(() => {
    setSelectedAssignee(
      greets.length == 1 ? first(greets)?.assignee : undefined
    );
  }, [greets]);

  const { mutateAsync: assignGreets } = useMutation(assignGreetsAction, {
    onSuccess: () => {
      queryClient.invalidateQueries('todos');
      queryClient.invalidateQueries(queryKeys.all);
    },
  });
  const { mutateAsync: assignWorkspaceGreets } = useMutation(
    assignWorkspaceGreetsAction,
    {
      onSuccess: () => {
        queryClient.invalidateQueries('todos');
        queryClient.invalidateQueries(queryKeys.all);
      },
    }
  );

  const { mutateAsync: disableFallback, isLoading: isDisablingGreetFallbacks } =
    useDisableGreetFallback({
      options: {
        onSuccess: () => {
          queryClient.invalidateQueries('todos');
          queryClient.invalidateQueries(queryKeys.all);

          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (bulkSelect.data.any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
            bulkSelect.uncheckAll();
          }

          history.replace('/home/greet/redirect');
        },
      },
    });

  const onConfirmDisableFallback = () => {
    analytics.track(GREET_DISABLE_FALLBACK_CONFIRM);

    disableFallback(greetIds);
    setShowConfirmationModal(false);
  };

  async function handleChangeAssignee(assignee?: UserSimple) {
    await assignGreets({ greetIds, assigneeId: assignee?.id ?? undefined });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (bulkSelect.data.any) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      bulkSelect.uncheckAll();
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (greetIds.length > 1 || bulkSelect.data.any) {
      history.replace('/home/greet/redirect');
    }
  }

  const handleChangeWorkspace = useCallback(
    async (workspace: Workspace | null) => {
      if (!workspace) return;

      try {
        await assignWorkspaceGreets({ greetIds, campaignId: workspace.id });

        setWorkspace(workspace);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (bulkSelect.data.any) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
          bulkSelect.uncheckAll();
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        if (greetIds.length > 1 || bulkSelect.data.any) {
          history.replace('/home/greet/redirect');
        }
      } catch (e) {
        if (isAxiosError<{ error: { message: string } }>(e)) {
          const { status, statusText, data } = e.response!;

          await alertAsync(
            <div>
              <h1>Error creating workspace</h1>
              <h2>{`${status}: ${statusText}`}</h2>
              <p>{data.error.message}</p>
            </div>
          );
        } else {
          throw e;
        }
      }
    },
    [greetIds]
  );

  const onDisableFallbackClick = () => {
    analytics.track(GREET_DISABLE_FALLBACK);
    setShowConfirmationModal(true);
  };

  return (
    <div
      className={`flex gap-2.5 px-3 py-2 flex-wrap justify-end ${className}`}
    >
      {showRollUp && <RollUpButtonWithTooltip greets={greets} />}

      {showAssign && (
        <TeamMemberSelector
          bodyPlaceholder='Assign'
          selectedUser={selectedAssignee}
          onSelect={handleChangeAssignee}
          hideAllSelector
          showPlaceholderAsSelected
          bodyContainerClassName='border border-solid border-neutral-500'
          width={100}
        />
      )}
      {showWorkspace && (
        <WorkspaceDropdown
          onWorkspaceChange={handleChangeWorkspace}
          selectedWorkspace={selectedWorkspace}
          label='Move'
          hideAllOption
          showLabelAsSelected
          width={120}
          dropdownBodyProps={{
            className: 'border border-solid border-neutral-500',
            contentClassName: 'pb-[6px]',
          }}
        />
      )}
      <Button
        variant={ButtonVariant.Secondary}
        onClick={handleDeleteClick}
        className='w-24 h-10 py-0'
      >
        Dismiss
      </Button>
      {isFallbackVideosEnabled && (
        <Tooltip
          disabled={hasFallback}
          bubbleClassName='v2-text-center'
          content={
            <>
              This feature lets you send a generic video after a time period you
              choose. You don&apos;t currently have this enabled,&nbsp;
              <a
                target='_blank'
                rel='noopener noreferrer'
                href='http://help.bonjoro.com/en/articles/10212469-fallback-videos'
              >
                click to learn more.
              </a>
            </>
          }
          positions={['left']}
        >
          <Button
            variant={ButtonVariant.Secondary}
            onClick={onDisableFallbackClick}
            disabled={!hasFallback}
            className='py-1.5 h-full'
          >
            Disable fallback
          </Button>
        </Tooltip>
      )}
      <DeleteConfirmationModal
        open={showConfirmationModal}
        title={
          <>
            Are you sure you want to disable this fallback? You won&apos;t be
            able to undo this.
          </>
        }
        onConfirm={onConfirmDisableFallback}
        onCancel={() => setShowConfirmationModal(false)}
        loading={isDisablingGreetFallbacks}
        confirmCtaText='Disable'
        hideIcon
      />
    </div>
  );
}
